import React from 'react';
import { BUSINESS, GRIEVANCE, fullAddress } from './businessInfo';

// Privacy Policy.
//
// Describes what this app actually does today: it collects a name, mobile number
// and delivery address for the order, keeps the cart in the browser, and hands
// payment off to the gateway without ever seeing card details.
//
// Keep it truthful as the app changes. If analytics, marketing messages or a
// third-party delivery partner are added later, they belong in section 3 or 4.
//
// One <section> per numbered heading: spacing on a phone, cards of a grid on a
// computer. The date and the opening paragraph span the page above them.

const PrivacyPolicy = () => (
    <div className="so-infopage">
        <p className="so-infopage__meta">
            Last updated: {BUSINESS.lastUpdated}
        </p>

        <p className="so-infopage__lead">
            This policy explains what {BUSINESS.brandName} does with your information when
            you order through this site. The business responsible is{' '}
            <strong>{BUSINESS.legalName || BUSINESS.fullName}</strong>, {fullAddress()}.
        </p>

        <section className="so-infopage__section">
            <h3>1. What we collect</h3>
            <ul>
                <li><strong>Your name and mobile number</strong>, so we can identify the order and call you about it.</li>
                <li><strong>A delivery address</strong>, when you choose delivery.</li>
                <li><strong>Your order</strong> — the items, quantities, the date you want them and any instructions you add.</li>
                <li><strong>Payment result</strong> — whether the payment succeeded, its reference and the amount.</li>
            </ul>
            <p>
                We do <strong>not</strong> collect or store your card number, CVV, PIN, UPI PIN or
                net-banking password. Those go directly to the payment gateway, which is certified
                to handle them.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>2. Why we use it</h3>
            <ul>
                <li>To prepare, confirm, deliver and, where needed, refund your order.</li>
                <li>To contact you about that order — a call, an SMS or a WhatsApp message about timing or a problem.</li>
                <li>To keep accounts and tax records the law requires us to keep.</li>
                <li>To investigate a disputed payment or a suspected fraudulent order.</li>
            </ul>
            <p>
                We do not sell your information, and we do not send marketing messages unless you
                ask us to.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>3. Who else sees it</h3>
            <ul>
                <li>
                    <strong>Our payment gateway</strong>, to take the payment and process any
                    refund. It handles your payment details under its own privacy policy.
                </li>
                <li>
                    <strong>Our delivery staff</strong>, who see the name, phone number and address
                    needed to deliver the order.
                </li>
                <li>
                    <strong>Authorities or our advisors</strong>, where the law requires it or to
                    defend a legal claim.
                </li>
            </ul>
        </section>

        <section className="so-infopage__section">
            <h3>4. Your device</h3>
            <p>
                This site keeps your cart and your sign-in on your own device so you do not lose
                them when you move between screens. Clearing your browser data clears them. We do
                not use advertising trackers on the ordering pages.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>5. How long we keep it</h3>
            <p>
                Order and payment records are kept as long as our accounting and tax obligations
                require, and then removed or anonymised. Contact details are kept with the order
                they belong to.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>6. Keeping it safe</h3>
            <p>
                Orders travel over an encrypted (HTTPS) connection, and access to order data is
                limited to restaurant staff who need it. No system is completely secure, but if a
                breach affects your information we will act on it and tell you where we are
                required to.
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>7. Your choices</h3>
            <ul>
                <li>Ask us what information we hold about you, and ask for a copy.</li>
                <li>Ask us to correct something that is wrong.</li>
                <li>
                    Ask us to delete information we are not required to keep — note that we cannot
                    delete the record of a paid order while tax law requires it.
                </li>
                <li>Ask us to stop messaging you about anything other than an active order.</li>
            </ul>
            <p>
                {BUSINESS.email
                    ? <>Write to {BUSINESS.email} and we will respond.</>
                    : <>Call us on {BUSINESS.phone} and we will act on it.</>}
            </p>
        </section>

        <section className="so-infopage__section">
            <h3>8. Children</h3>
            <p>
                This site is meant for adults. We do not knowingly collect information from
                children; if a child has given us their details, contact us and we will remove them.
            </p>
        </section>

        {/* Only once there is an officer to name — see GRIEVANCE in businessInfo.js. */}
        {GRIEVANCE.name && (
            <section className="so-infopage__section">
                <h3>9. Grievances</h3>
                <p>
                    If you are not satisfied with how we have handled your information,
                    escalate to {GRIEVANCE.name}, {GRIEVANCE.designation}
                    {GRIEVANCE.email ? ' — ' + GRIEVANCE.email : ''}
                    {GRIEVANCE.phone ? ', ' + GRIEVANCE.phone : ''}. We aim to respond
                    within {GRIEVANCE.responseHours} hours.
                </p>
            </section>
        )}

        <section className="so-infopage__section">
            <h3>10. Changes</h3>
            <p>
                We will update this page when our practices change, and the date at the top will
                tell you when it last changed.
            </p>
        </section>
    </div>
);

export default PrivacyPolicy;
